import { Routes } from '@generouted/react-router';
import { SidebarProvider } from '@report/app-frame';
import { theme } from '@report/components';
import { ConfigProvider } from '@report/configuration';
import * as Sentry from '@sentry/react';
import { ApolloProvider, createApolloClient } from '@shared/apollo';
import { AuthenticationProvider, getExternalForceLogout } from '@shared/auth';
import { CssBaseline, ThemeProvider } from '@shared/components';
import { LoggerProvider } from '@shared/logger';
import { PermissionProvider } from '@shared/permissions';
import { SnackbarProvider } from '@shared/snackbar';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';

import { configuration } from './configuration';

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn && !import.meta.env.DEV) {
  Sentry.init({
    dsn,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
  });
}

const {
  graphql: { apiUrl, defaultFetchPolicy = 'cache-first' },
  logLevel,
} = configuration;

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const client = createApolloClient(
  apiUrl,
  {
    query: { fetchPolicy: defaultFetchPolicy ?? 'cache-first' },
    watchQuery: { fetchPolicy: defaultFetchPolicy ?? 'cache-first' },
  },
  {
    onUnauthorized: () => getExternalForceLogout()?.(),
  },
);

root.render(
  <StrictMode>
    <ConfigProvider config={configuration}>
      <LoggerProvider config={{ level: logLevel, enableConsole: true }}>
        <ApolloProvider client={client}>
          <NuqsAdapter>
            <AuthenticationProvider>
              <PermissionProvider>
                <ThemeProvider theme={theme}>
                  <SidebarProvider>
                    <SnackbarProvider
                      options={{
                        snackbarVariant: {
                          pending: 'muted',
                        },
                      }}
                    >
                      <CssBaseline />
                      <Routes />
                    </SnackbarProvider>
                  </SidebarProvider>
                </ThemeProvider>
              </PermissionProvider>
            </AuthenticationProvider>
          </NuqsAdapter>
        </ApolloProvider>
      </LoggerProvider>
    </ConfigProvider>
  </StrictMode>,
);
